import { useMemo } from 'react'
import styled from '@emotion/styled'

import { A } from '../A'
import { Flex } from '../Flex'
import { Item } from '../Typography'

import { colors, ItemConfig } from '@/constants'
import { SIDEBAR_CONFIG } from '@/constants/sideBarConfig'
import { mediaQuery } from '@/utils/mediaQuery'
import { getItemUrl } from '@/utils/subMenu'
import { useUserCapabilities } from '@/hooks/useUserCapabilities'
import { useLogout } from '@/features/Auth/application/useLogout'
import { LogoutIcon } from '@/assets/icons'

const IconWithText = styled(Flex)<{
  isActive?: boolean
  disabled?: boolean
  isSubSidebar?: boolean
}>`
  gap: 0.625rem;
  width: 100%;
  display: flex;
  height: 3.375rem;
  padding: 0.9375rem;
  align-items: center;
  align-self: stretch;
  border-radius: 1rem;
  transition: all 0.3s ease;
  color: ${colors.light2};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ isActive }) =>
    isActive &&
    `
      background-color: ${colors.darkBlue2};
      color: ${colors.white};
    `}

  ${({ disabled }) =>
    disabled
      ? `opacity: 0.5;`
      : `
    cursor: pointer;
    &:hover {
      background-color: ${colors.darkBlue3};
    }
  `}

  ${({ isSubSidebar }) => mediaQuery(isSubSidebar ? 'sm' : 'lg')} {
    ${Item} {
      display: none;
    }
  }

  a:focus-visible > & {
    box-shadow: 0 0 0 2px ${colors.blue};
    outline: none;
  }
`

const Footer = styled(Flex)`
  flex-direction: column;
  justify-self: end;
`

type ContentProps = {
  location: string
  withIcon?: boolean
  config?: ItemConfig[]
  isSubSidebar?: boolean
  sideBarOpen?: boolean
  sideBarHover?: boolean
}

const useFilteredConfig = (config: ItemConfig[]) => {
  const capabilities = useUserCapabilities()

  return useMemo(() => {
    if (!capabilities) return []

    return config.filter((item) => {
      return !item.capability || capabilities[item.capability]
    })
  }, [config, capabilities])
}

export default function SideBarContent({
  config = SIDEBAR_CONFIG,
  isSubSidebar,
  location,
  withIcon = true,
  sideBarOpen = true,
  sideBarHover = false,
}: ContentProps) {
  const filteredConfig = useFilteredConfig(config)
  const logout = useLogout()

  return (
    <Flex
      flexGrow={1}
      justifyContent="space-between"
      flexDirection="column"
      mx="0.9375rem"
      my="1.25rem"
    >
      <Flex flexDirection="column" gap="0.35rem">
        {filteredConfig
          .filter((entry) => !entry.footer)
          .map((item) => (
            <A href={getItemUrl(item)} key={`sidebar-item-${item.label}`}>
              <IconWithText
                isActive={location === getItemUrl(item)}
                isSubSidebar={isSubSidebar}
              >
                {withIcon && item.icon}
                {sideBarOpen || sideBarHover ? <Item>{item.label}</Item> : null}
              </IconWithText>
            </A>
          ))}
      </Flex>
      <Footer>
        {filteredConfig
          .filter((entry) => entry.footer)
          .map((item) => (
            <A href={getItemUrl(item)} key={`sidebar-item-${item.label}`}>
              <IconWithText isActive={location === getItemUrl(item)}>
                {withIcon && item.icon}
                {sideBarOpen || sideBarHover ? <Item>{item.label}</Item> : null}
              </IconWithText>
            </A>
          ))}

        {!isSubSidebar && (
          <IconWithText
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                logout()
              }
            }}
            onClick={logout}
          >
            {withIcon && <LogoutIcon />}
            {sideBarOpen || sideBarHover ? <Item>Log out</Item> : null}
          </IconWithText>
        )}
      </Footer>
    </Flex>
  )
}
